import { useComparisonPeriod } from 'src/utils/Hooks';
import { calculatePercentChange } from 'src/utils/app';
import { useDorisData } from 'src/utils/Hooks/useDorisData';

export type MetricType = 'ClicksSummary';

export interface TrafficSummaryResponse {
  metricType: MetricType;
  totalClicks?: number;
  organicClicks?: number;
  adClicks?: number;
  otherClicks?: number;
  totalConversions?: number;
  organicConversions?: number;
  debug?: {
    query: string;
    queryExecutionTime: number;
    queryContextCreationTime: number;
    isCacheHit: boolean;
    cacheResponseTime: number;
    topicGroupAnnotationTime: number;
  };
  [key: string]: any;
}

// Parameter interface for filter-related fields
export interface FilterParams {
  categoryIds: number[];
  subcategoryIds: number[];
  brandIds: number[];
  segmentIds: string[];
  minRetailPrice?: number;
  maxRetailPrice?: number;
  keywords?: string[];
  businessunitids?: string[];
}

// The parameter type for our hook
export interface UseDorisSummaryParams {
  requestName: string;
  metricType: MetricType;
  retailerId: number;
  categoryIds?: number[];
  subcategoryIds?: number[];
  brandIds?: number[];
  segmentIds?: string[];
  businessunitids?: string[];

  minRetailPrice?: number;
  maxRetailPrice?: number;
  keywords?: string[];

  // Optional time range - if not provided, will use the time range from redux store
  startWeekId?: number;
  endWeekId?: number;
  comparisonStartWeekId?: number;
  comparisonEndWeekId?: number;

  resultCount?: number;

  /**
   * When true, only fetches the primary data without comparison
   */
  skipComparison?: boolean;

  /**
   * Optional field name to use for the main metric value
   * If not provided, it will be automatically selected based on metricType
   * @example 'totalClicks', etc.
   */
  metricField?: string;

  /**
   * Function to transform primary data into parameters for comparison query
   */
  getComparisonParamsFromPrimary?: (primaryData: TrafficSummaryResponse) => {
    categoryIds?: number[];
    subcategoryIds?: number[];
    brandIds?: number[];
    segmentIds?: string[];
  };

  /**
   * Function to process the raw data before returning
   */
  processData?: (data: TrafficSummaryResponse) => any;

  /**
   * Optional settings for the query
   */
  queryOptions?: {
    enabled?: boolean;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    retry?: boolean | number;
  };

  /**
   * Callback to intercept and modify base parameters before the primary query
   */
  interceptBaseParams?: (params: FilterParams) => FilterParams;

  /**
   * Callback to intercept and modify comparison parameters before the comparison query
   */
  interceptComparisonParams?: (params: FilterParams) => FilterParams;
}

/**
 * Default metric fields mapping for each metric type
 */
const DEFAULT_METRIC_FIELD_MAP: Record<MetricType, string> = {
  ClicksSummary: 'totalClicks'
};

/**
 * Hook for fetching and processing traffic summary data
 */
export const useDorisSummary = ({
  metricType,
  requestName,
  categoryIds = [],
  subcategoryIds = [],
  brandIds = [],
  segmentIds = [],
  businessunitids = [],
  minRetailPrice,
  maxRetailPrice,
  startWeekId: customStartWeekId,
  endWeekId: customEndWeekId,
  comparisonStartWeekId: customComparisonStartWeekId,
  comparisonEndWeekId: customComparisonEndWeekId,
  resultCount = 1200,
  skipComparison = false,
  metricField,
  getComparisonParamsFromPrimary,
  processData,
  queryOptions = {},
  interceptBaseParams,
  interceptComparisonParams
}: UseDorisSummaryParams) => {
  const comparisonPeriod = useComparisonPeriod();
  const metricFieldToUse = metricField || DEFAULT_METRIC_FIELD_MAP[metricType];

  // Use the generic useDorisData hook with our specific parameters
  const {
    data: primaryData,
    comparisonData,
    isLoading,
    error
  } = useDorisData<TrafficSummaryResponse>({
    methodName: 'GetTrafficCountSummary',
    metricType,
    queryKey: requestName,
    resultCount,
    categoryIds,
    subcategoryIds,
    brandIds,
    segmentIds,
    minRetailPrice,
    maxRetailPrice,
    keywords: [],
    businessunitids,
    enabled: queryOptions.enabled !== false,
    customTimePeriods: {
      startWeekId: customStartWeekId,
      endWeekId: customEndWeekId,
      comparisonStartWeekId: customComparisonStartWeekId,
      comparisonEndWeekId: customComparisonEndWeekId
    },
    skipComparison,
    additionalParams: {
      interceptBaseParams,
      interceptComparisonParams,
      ...(getComparisonParamsFromPrimary && { getComparisonParamsFromPrimary })
    },
    queryOptions: {
      refetchOnWindowFocus: queryOptions.refetchOnWindowFocus ?? false,
      staleTime: queryOptions.staleTime ?? 5 * 60 * 1000,
      retry: queryOptions.retry
    }
  });

  // Process data if needed
  const processedPrimaryData = processData && primaryData ? processData(primaryData) : primaryData;
  const processedComparisonData = processData && comparisonData ? processData(comparisonData) : comparisonData;

  // Extract values and calculate percent change
  const primaryValue = processedPrimaryData ? processedPrimaryData[metricFieldToUse] : 0;
  const comparisonValue = skipComparison || !processedComparisonData ? 0 : processedComparisonData[metricFieldToUse];
  const percentChange = skipComparison ? 0 : calculatePercentChange(primaryValue, comparisonValue);

  return {
    primaryData: processedPrimaryData,
    comparisonData: processedComparisonData,
    primaryValue,
    comparisonValue,
    percentChange,
    isLoading,
    isError: !!error,
    error,
    metricField: metricFieldToUse,
    comparisonPeriod
  };
};
